/**
 * Phase 8 follow-up (21-Apr-26) — "Also a fit for" fallback.
 *
 * The N×M `runMultiJdSearch` output exposes every in-memory alternate on
 * `MatchResult` rows, which the canvas groups into `alternatesByCandidate`
 * to render the `MatchCard` footer. That works when the search touches
 * more than one JD — but for the very common case of a single-JD search
 * (the screenshot on 21-Apr), every match shares the same `job_id`, so
 * the in-memory alternates collapse to an empty list and the footer
 * silently disappears.
 *
 * This hook plugs that gap by batch-querying `ggj_b2b_match_results` for
 * the *visible* candidate set. The service returns the last persisted
 * top-N roles per candidate (N = 5), sorted by `composite_score DESC`,
 * so the canvas can still answer "what else is this candidate a fit
 * for?" even when the current search spans a single role.
 *
 * Keyed by `(orgId, candidateIdSignature)` so navigating back to an
 * earlier thread reuses the cached snapshot instead of re-firing the
 * query.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { queryKeys } from '@core/query/queryKeys';
import { matchResultService } from '@core/supabase/services/matchResultService';
import type { MatchResult } from '../types';

const EMPTY = new Map<string, MatchResult[]>();

/**
 * Signature the query key uses — stable hash-ish string so the cache
 * dedupes equivalent candidate sets regardless of the order they were
 * passed in. Keeping this out of the main service so the service stays
 * a thin data layer.
 */
function signatureFor(ids: string[]): string {
  const unique = Array.from(new Set(ids.filter(Boolean))).sort();
  if (unique.length === 0) return 'empty';
  if (unique.length <= 8) return unique.join(',');
  // Cap the key cardinality for very large sets — use the first/last
  // plus the total count so different ordering is still cache-hit.
  return `${unique[0]}..${unique[unique.length - 1]}:${unique.length}`;
}

interface Options {
  /** Skip when the canvas hasn't settled yet (prevents flicker). */
  enabled?: boolean;
  /** Per-candidate slice; default 5 so card footers stay compact. */
  perCandidateLimit?: number;
}

export function useAlternateJobsForCandidates(
  candidateIds: string[],
  opts: Options = {},
): {
  data: Map<string, MatchResult[]>;
  isLoading: boolean;
  isFetching: boolean;
} {
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const enabled = (opts.enabled ?? true) && !!orgId && candidateIds.length > 0;
  const signature = useMemo(() => signatureFor(candidateIds), [candidateIds]);
  const limit = opts.perCandidateLimit ?? 5;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: queryKeys.smartMatch.altJobsForCandidates(orgId ?? '__none__', signature),
    queryFn: () =>
      matchResultService.getForCandidates(orgId as string, candidateIds, limit),
    enabled,
    // Short stale time so the post-search invalidation (from
    // useSmartMatchSearch) refreshes the footer promptly once the
    // background crossScoreOrgCandidatesVsJobs write completes.
    staleTime: 10_000,
    gcTime: 5 * 60_000,
  });

  return {
    data: data ?? EMPTY,
    isLoading: !!isLoading,
    isFetching: !!isFetching,
  };
}
